import { getCartErrorMessage } from '@theme/cart-feedback';

// <s-quick-add>: body of the quick-add panel. Any button with [data-quick-add="<handle>"] (product cards,
// including cards appended by infinite scroll) loads that product's JSON and renders image, price,
// option buttons and quantity here; Add to cart uses Shopify.actions.updateCart then opens the cart drawer.
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.dialog = this.closest('dialog');
    this.loading = this.querySelector('[data-qa-loading]');
    this.body = this.querySelector('[data-qa-body]');
    this.onClick = (event) => {
      const trigger = event.target.closest('[data-quick-add]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      this.open(trigger.dataset.quickAdd, trigger);
    };
    document.addEventListener('click', this.onClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick, true);
  }

  money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = Number(cents) / 100;
    const withDelims = (n, d, t = ',', dec = '.') => {
      const [i, f] = n.toFixed(d).split('.');
      const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, t);
      return f ? `${int}${dec}${f}` : int;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0);
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        default: return withDelims(value, 2);
      }
    });
  }

  // Phones: fetch first, then open the sheet already filled in,
  // with the card's bag button pulsing while loading. Larger screens: open immediately with the spinner.
  async open(handle, trigger) {
    if (!this.dialog) return;
    const phone = window.matchMedia('(max-width: 767px)').matches;
    if (phone && this.busy) return;
    this.product = null;
    this.body.hidden = true;
    this.loading.hidden = phone;
    if (!phone && !this.dialog.open) this.dialog.showModal();
    if (phone) { this.busy = true; trigger?.setAttribute('aria-busy', 'true'); }
    try {
      const res = await fetch(`${this.dataset.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) throw new Error(String(res.status));
      this.product = await res.json();
      const first = this.product.variants.find((v) => v.available) || this.product.variants[0];
      this.selected = [...first.options];
      this.quantity = 1;
      this.render();
    } catch {
      this.body.innerHTML = `<p class="text-sm text-sale">${escapeHtml(this.dataset.errorLabel)}</p>`;
      this.body.hidden = false;
    } finally {
      this.loading.hidden = true;
      if (phone) {
        this.busy = false;
        trigger?.removeAttribute('aria-busy');
        if (!this.dialog.open) this.dialog.showModal();
      }
    }
  }

  currentVariant() {
    return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i]));
  }

  valueAvailable(optionIndex, value) {
    return this.product.variants.some((v) => v.available && v.options[optionIndex] === value &&
      v.options.every((o, i) => i === optionIndex || o === this.selected[i]));
  }

  render() {
    const p = this.product;
    const v = this.currentVariant();
    const image = (v?.featured_image?.src) || p.featured_image;
    const imgSrc = image ? `${image}${image.includes('?') ? '&' : '?'}width=600` : '';
    const showOptions = !(p.variants.length === 1 && p.options[0]?.name === 'Title');
    const price = v ? this.money(v.price) : '';
    const compare = v && v.compare_at_price > v.price ? this.money(v.compare_at_price) : '';

    const saving = v && v.compare_at_price > v.price ? v.compare_at_price - v.price : 0;
    const percent = saving ? Math.round((saving * 100) / v.compare_at_price) : 0;
    const saveLabel = saving ? (this.dataset.saveLabel || '__amount__').replace('__amount__', this.money(saving)) : '';
    const ico = (path) => `<svg class="size-5 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
    const perks = [
      [ico('<path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.5"/><circle cx="17" cy="17.5" r="1.5"/>'), this.dataset.perkShipping],
      [ico('<path d="M4 9a8 8 0 0 1 14-3l2 2M20 15a8 8 0 0 1-14 3l-2-2"/><path d="M20 4v4h-4M4 20v-4h4"/>'), this.dataset.perkExchange],
      [ico('<rect x="5" y="11" width="14" height="9" rx="1"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>'), this.dataset.perkSecure],
    ].filter(([, label]) => label);

    const options = showOptions ? p.options.map((opt, i) => `
      <fieldset class="m-0 flex flex-col gap-2 border-0 p-0">
        <legend class="mb-2 text-[0.6875rem] font-medium uppercase tracking-[0.18em] text-on-surface md:text-xs md:tracking-[0.12em]">${escapeHtml(opt.name)}: <span class="font-semibold text-accent normal-case tracking-normal">${escapeHtml(this.selected[i])}</span></legend>
        <div class="flex flex-wrap gap-2">
          ${opt.values.map((val) => {
            const active = this.selected[i] === val;
            const available = this.valueAvailable(i, val);
            return `<button type="button" data-qa-option="${i}" data-value="${escapeHtml(val)}" aria-pressed="${active}"
              class="relative inline-flex h-11 min-w-12 cursor-pointer items-center justify-center border px-3 text-[0.8125rem] font-medium md:text-sm motion-safe:transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent
              ${active ? 'border-on-surface bg-on-surface text-surface' : 'border-on-surface/20 bg-surface text-on-surface hover:border-accent hover:text-accent'}
              ${available ? '' : 'text-on-surface/40 line-through'}">${escapeHtml(val)}</button>`;
          }).join('')}
        </div>
      </fieldset>`).join('') : '';

    const available = Boolean(v?.available);
    this.body.innerHTML = `
      <div class="flex flex-col gap-5">
        <div class="flex gap-4">
          <div class="relative w-28 shrink-0 overflow-hidden bg-surface-secondary md:w-32">
            ${imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="${escapeHtml(p.title)}" class="aspect-[3/4] w-full object-cover" width="600" height="800">` : ''}
            ${percent ? `<span class="absolute start-1.5 top-1.5 bg-accent px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-[0.12em] text-white">-${percent}%</span>` : ''}
          </div>
          <div class="flex min-w-0 flex-col gap-2">
            <h3 id="quick-add-title" class="m-0 text-sm font-medium uppercase leading-snug tracking-wide text-on-surface md:text-base md:normal-case md:tracking-normal">${escapeHtml(p.title)}</h3>
            <p class="m-0 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span class="text-base font-semibold md:text-lg ${compare ? 'text-accent' : 'text-on-surface'}">${escapeHtml(price)}</span>
              ${compare ? `<s class="text-xs font-normal text-on-surface-secondary md:text-sm">${escapeHtml(compare)}</s>` : ''}
            </p>
            ${saveLabel ? `<span class="w-fit bg-accent/10 px-2 py-1 text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-accent">${escapeHtml(saveLabel)}</span>` : ''}
            <a href="${escapeHtml(p.url)}" class="mt-auto text-[0.6875rem] font-medium uppercase tracking-[0.18em] md:text-xs md:tracking-[0.12em] text-on-surface underline decoration-accent underline-offset-4 hover:text-accent">${escapeHtml(this.dataset.detailsLabel)}</a>
          </div>
        </div>
        ${options}
        ${perks.length ? `<ul role="list" class="m-0 grid list-none grid-cols-3 gap-2 bg-surface-secondary p-3 text-center">
          ${perks.map(([icon, label]) => `<li class="flex flex-col items-center gap-1.5 text-[0.625rem] font-medium uppercase leading-tight tracking-[0.1em] text-on-surface">${icon}<span>${escapeHtml(label)}</span></li>`).join('')}
        </ul>` : ''}
        <div class="sticky bottom-0 flex flex-col gap-2 bg-surface pb-1 pt-1">
          <div class="flex items-stretch gap-2">
            <div class="inline-flex h-12 shrink-0 items-center border border-on-surface/20" role="group" aria-label="${escapeHtml(this.dataset.quantityLabel)}">
              <button type="button" data-qa-qty="-1" class="inline-flex h-full w-10 cursor-pointer items-center justify-center text-lg text-on-surface hover:text-accent" aria-label="${escapeHtml(this.dataset.decreaseLabel)}">−</button>
              <span class="w-6 text-center text-[0.8125rem] font-semibold tabular-nums md:text-sm" aria-live="polite">${this.quantity}</span>
              <button type="button" data-qa-qty="1" class="inline-flex h-full w-10 cursor-pointer items-center justify-center text-lg text-on-surface hover:text-accent" aria-label="${escapeHtml(this.dataset.increaseLabel)}">+</button>
            </div>
            <button type="button" data-qa-add ${available ? '' : 'disabled'} class="inline-flex h-12 min-w-0 flex-1 cursor-pointer items-center justify-center gap-2 bg-accent px-3 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-white md:text-xs md:tracking-[0.14em] hover:bg-on-surface disabled:cursor-not-allowed disabled:bg-on-surface/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-surface motion-safe:transition-colors">
              ${available ? '<svg class="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8h14l-1 12H6L5 8Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg>' : ''}
              <span class="truncate">${escapeHtml(available ? `${this.dataset.addLabel} · ${price}` : this.dataset.soldOutLabel)}</span>
            </button>
          </div>
          <p data-qa-error role="alert" hidden class="m-0 text-sm text-sale"></p>
        </div>
      </div>`;
    this.body.hidden = false;
    this.bind();
  }

  bind() {
    this.body.querySelectorAll('[data-qa-option]').forEach((btn) => btn.addEventListener('click', () => {
      const i = Number(btn.dataset.qaOption);
      this.selected[i] = btn.dataset.value;
      if (!this.currentVariant()) {
        const match = this.product.variants.find((v) => v.options[i] === btn.dataset.value && v.available) ||
          this.product.variants.find((v) => v.options[i] === btn.dataset.value);
        if (match) this.selected = [...match.options];
      }
      this.render();
      this.body.querySelector(`[data-qa-option="${i}"][aria-pressed="true"]`)?.focus();
    }));
    this.body.querySelectorAll('[data-qa-qty]').forEach((btn) => btn.addEventListener('click', () => {
      this.quantity = Math.max(1, Math.min(99, this.quantity + Number(btn.dataset.qaQty)));
      this.render();
      this.body.querySelector(`[data-qa-qty="${btn.dataset.qaQty}"]`)?.focus();
    }));
    this.body.querySelector('[data-qa-add]')?.addEventListener('click', () => this.add());
  }

  async add() {
    const v = this.currentVariant();
    const button = this.body.querySelector('[data-qa-add]');
    const error = this.body.querySelector('[data-qa-error]');
    if (!v || !v.available || !button) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: String(v.id), quantity: this.quantity }] },
        { event: { context: 'product', detail: { source: 's-quick-add', productId: String(this.product.id) } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) throw new Error(message);
      this.dialog.close();
      await window.Shopify.actions.openCart?.();
    } catch (e) {
      error.textContent = getCartErrorMessage(e) || e.message || this.dataset.errorLabel;
      error.hidden = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-quick-add')) customElements.define('s-quick-add', QuickAdd);
